/**
 * ACL Views — Central source of truth
 *
 * Keys are stored verbatim in `companies_acl_level_rights.val` (comma-separated).
 * Add new entries here when new top-level sections are added to the router.
 *
 * ── Granular sub-views ────────────────────────────────────────────────────────
 * A top-level view may declare `subViews` to gate individual areas inside it
 * (e.g. Kunden → Liste, CRM, Insights). Sub-view grants are stored in the SAME
 * `views` val string using a dotted namespace: `Kunden.Liste,Kunden.CRM`.
 *
 * Backwards compatibility: a level that has the parent key but NO sub-view keys
 * for it is treated as having FULL access to every sub-view (legacy behaviour) —
 * see `hasSubViewAccess()`. Explicit sub-view keys switch it to allow-list mode.
 */
export const ACL_VIEWS = {
  Kalender: {
    label: 'Kalender',
    description: 'Kurskalender & Serien',
    route: '/calendar',
  },
  Kunden: {
    label: 'Kunden',
    description: 'Kundenliste, CRM & Kundendetails',
    route: '/clients',
    subViews: {
      Liste: {
        label: 'Liste',
        description: 'Kundenliste & Kundendetails',
        route: '/clients/overview',
        menuHeading: 'Kunden',
      },
      CRM: {
        label: 'CRM',
        description: 'Anfragemanagement, Interessenten & Newsletter',
        route: '/clients/crm',
        menuHeading: 'CRM',
      },
      Insights: {
        label: 'Insights',
        description: 'Auswertungen, Umsatz, Verträge & Statistiken',
        route: '/clients/insights',
        menuHeading: 'Insights',
      },
      // Zusätzlich zur ACL an die Trainingsart gekoppelt: ohne freies Training
      // gibt es keinen Einlass, dann ist der Punkt unabhängig vom Recht weg
      // (useTrainingModes → Aside-Filter + Router-Guard).
      Einlass: {
        label: 'Einlass',
        description: 'Checkin/Checkout & Anwesenheit (freies Training)',
        route: '/clients/attendance',
        menuHeading: 'Einlass',
      },
    },
  },
  Leistungen: {
    label: 'Leistungen & Training',
    description: 'Kurse, Produkte, Übungen, Workouts, Trainingspläne & Todos',
    route: '/work',
    subViews: {
      Uebersicht: {
        label: 'Übersicht',
        description: 'Leistungs-Übersichtsseite',
        route: '/work/overview',
        menuHeading: 'Übersicht',
      },
      Angebot: {
        label: 'Angebot',
        description: 'Leistungen/Trainings, Produkte & Produktkategorien',
        route: '/work/services',
        menuHeading: 'Angebot',
      },
      Trainings: {
        label: 'Trainings',
        description: 'Übungen, Workouts & Trainingspläne',
        route: '/work/trainings/exercises',
        menuHeading: 'Trainings',
      },
      Arbeitsplanung: {
        label: 'Arbeitsplanung',
        description: 'ToDos, TeamPlanner & Trainingsreports',
        route: '/work/tasks',
        menuHeading: 'Arbeitsplanung',
      },
    },
  },
  Finanzen: {
    label: 'Finanzen',
    description: 'Verkauf, Rechnungen, Zahlungsabgleich, Sammelüberweisungen, Mahnwesen',
    route: '/finance',
    subViews: {
      // ACL-Key bleibt 'Kasse' (in den Berechtigungsdaten persistiert) — nur das Label heißt jetzt "Verkauf"
      Kasse: {
        label: 'Verkauf',
        description: 'Kassensystem & Verkauf',
        route: '/finance/cashpoint',
      },
      Rechnungen: {
        label: 'Rechnungen',
        description: 'Rechnungsübersicht & -verwaltung',
        route: '/finance/salesinvoices',
      },
      Zahlungsabgleich: {
        label: 'Zahlungsabgleich',
        description: 'Offene Zahlungen abgleichen',
        route: '/finance/reconciliation',
      },
      Sammelueberweisungen: {
        label: 'Sammelüberweisungen',
        description: 'SEPA-Sammelüberweisungen erstellen & verwalten',
        route: '/finance/batch-transfers',
      },
      Mahnwesen: {
        label: 'Mahnwesen',
        description: 'Mahnläufe & offene Forderungen',
        route: '/finance/mahnwesen/overview',
        menuHeading: 'Mahnwesen',
      },
    },
  },
  Kommunikation: {
    label: 'Kommunikation',
    description: 'Chat, E-Mail-Posteingang',
    route: '/communication',
    subViews: {
      Posteingang: {
        label: 'E-Mail-Posteingang',
        description: 'Gmail- & IMAP/POP3-Postfächer',
        route: '/communication/inbox',
      },
      Chat: {
        label: 'Chat',
        description: 'Chat mit Kunden & Anfragen',
        route: '/communication/chat',
      },
      Newsletter: {
        label: 'Newsletter & Automation',
        description: 'Newsletter, Vorlagen & Automation Flows',
        route: '/communication/newsletter',
      },
      Kampagnen: {
        label: 'Kampagnen',
        description: 'Zielgruppen-Briefing und die daran haengenden Werbemittel',
        route: '/communication/campaigns',
      },
    },
  },
  Widgets: {
    label: 'Widgets',
    description: 'Buchungs- und Kurswidgets',
    route: '/widgets',
  },
  Unternehmen: {
    label: 'Unternehmen',
    description: 'Übersicht, Studios, Finanz-Stammdaten, Team, Öffnungszeiten, Medienbibliothek, Zugangslevel',
    route: '/company',
    subViews: {
      Verwaltung: {
        label: 'Verwaltung',
        description: 'Übersicht, Studios, Studiozeiten & Geräte',
        route: '/company/overview',
        menuHeading: 'Unternehmen',
      },
      Finanzen: {
        label: 'Finanzen',
        description: 'Kostenstruktur, Finanz-Einstellungen, Konten & Zahlungskonditionen',
        route: '/company/finance',
        menuHeading: 'Finanzen',
      },
      Mitarbeiter: {
        label: 'Mitarbeiter',
        description: 'Team, Zugriffslevel & TeamPlanner',
        route: '/company/team',
        menuHeading: 'Mitarbeiter',
      },
      Medien: {
        label: 'Medien',
        description: 'Medienbibliothek & Vorlagen-Katalog',
        route: '/company/medialibrary',
        menuHeading: 'Tools',
      },
    },
  },
  FormularProzesse: {
    label: 'Formular-Prozesse',
    description: 'Erstellen und Verwalten von Formular-Prozess-Vorlagen (Anamnesebogen, AGB, DSGVO etc.)',
    route: '/company',
  },
} as const;

/** Union type of all valid ACL view keys */
export type AclViewKey = keyof typeof ACL_VIEWS;

/** Shape of a single sub-view definition */
export interface AclSubView {
  label: string;
  description: string;
  route: string;
  /** Heading in the aside menu config this sub-view gates (optional) */
  menuHeading?: string;
}

/**
 * Legacy view keys still present in old `companies_acl_level_rights.val` data
 * (e.g. 'Dashboard,Kalender,…,Work,…,Settings'). Mapped to their current
 * equivalent at read time; keys without an equivalent are simply ignored.
 */
export const LEGACY_VIEW_ALIASES: Record<string, AclViewKey> = {
  Work: 'Leistungen',
};

/**
 * Legacy SUB-view keys that were split/renamed after release, mapped per parent
 * to their current equivalents. Expanded at read time in `parseAclRights`;
 * materialized to the new keys on the next save in the admin UI.
 */
export const LEGACY_SUBVIEW_ALIASES: Record<string, Record<string, string[]>> = {
  Finanzen: {
    // Former grouped sub-views, split into per-page sub-views
    Zahlungswesen: ['Kasse', 'Rechnungen', 'Zahlungsabgleich', 'Sammelueberweisungen'],
  },
};

/**
 * Sub-views that MOVED to a different parent view, mapped `oldParent → oldSub →
 * ['NewParent.NewSub', …]`. Applied as a post-pass in `parseAclRights` and — in
 * contrast to `LEGACY_SUBVIEW_ALIASES` — only if the TARGET parent view is
 * granted too, so a move never widens access to a section the level cannot see.
 *
 * Kostenstruktur & Zahlungskonditionen moved from the Finanzen aside menu into
 * the Unternehmen area (section "Finanzen") — see CompanyMenuConfig.
 */
export const LEGACY_SUBVIEW_MOVES: Record<string, Record<string, string[]>> = {
  Finanzen: {
    Kostenstruktur: ['Unternehmen.Finanzen'],
    Einstellungen: ['Unternehmen.Finanzen'],
    // pre-split grouped key (was aliased to Kostenstruktur + Einstellungen)
    Verwaltung: ['Unternehmen.Finanzen'],
  },
};

/**
 * Sub-view keys for a parent view (empty array if it has no sub-views).
 * Tolerates unknown keys — old `val` data may still contain dropped legacy
 * views (e.g. 'Dashboard', 'Settings') that have no ACL_VIEWS entry.
 */
export function subViewKeys(parent: AclViewKey): string[] {
  const sv = (ACL_VIEWS[parent] as any)?.subViews as Record<string, AclSubView> | undefined;
  return sv ? Object.keys(sv) : [];
}

/** Whether a parent view declares any sub-views */
export function hasSubViews(parent: AclViewKey): boolean {
  return subViewKeys(parent).length > 0;
}

/** Ordered sub-view entries for a parent (empty if none; unknown keys tolerated) */
export function subViewEntries(parent: AclViewKey): [string, AclSubView][] {
  const sv = (ACL_VIEWS[parent] as any)?.subViews as Record<string, AclSubView> | undefined;
  return sv ? (Object.entries(sv) as [string, AclSubView][]) : [];
}

/**
 * Parsed representation of a stored `views` val string, splitting top-level
 * view grants from dotted sub-view grants (`Kunden.Insights`).
 */
export interface ParsedAclViews {
  /** Top-level view keys (legacy keys normalized) */
  views: AclViewKey[];
  /** Map of parent view key → granted sub-view keys */
  subViews: Record<string, string[]>;
}

/** Split a stored `val` string into top-level views + per-parent sub-views. */
export function parseAclRights(val: string): ParsedAclViews {
  const views: AclViewKey[] = [];
  const subViews: Record<string, string[]> = {};

  (val || '')
    .split(',')
    .map(v => v.trim())
    .filter(Boolean)
    .forEach(token => {
      if (token.includes('.')) {
        const [rawParent, sub] = token.split('.');
        if (!sub) return;
        const parent = (LEGACY_VIEW_ALIASES[rawParent] ?? rawParent) as string;
        const aliased = LEGACY_SUBVIEW_ALIASES[parent]?.[sub];
        const target = (subViews[parent] ??= []);
        (aliased ?? [sub]).forEach(s => {
          if (!target.includes(s)) target.push(s);
        });
      } else {
        views.push((LEGACY_VIEW_ALIASES[token] ?? token) as AclViewKey);
      }
    });

  // Post-pass: carry grants for sub-views that moved to another parent over to
  // their new home. Runs after the token loop because the target parent's
  // top-level grant may appear anywhere in the string.
  //
  // The moved key is deliberately LEFT in the old parent's list: removing it
  // could empty that list, which `hasSubViewAccess` reads as "legacy /
  // unrestricted" and would silently grant every remaining sub-view there.
  Object.entries(LEGACY_SUBVIEW_MOVES).forEach(([oldParent, moves]) => {
    const stored = subViews[oldParent];
    if (!stored?.length) return;
    Object.entries(moves).forEach(([oldSub, targets]) => {
      if (!stored.includes(oldSub)) return;
      targets.forEach(dotted => {
        const [newParent, newSub] = dotted.split('.');
        if (!newSub || !views.includes(newParent as AclViewKey)) return;
        // Only extend a parent that already stores sub-keys (allow-list mode).
        // A parent without any is unrestricted and grants the moved sub-view
        // already — seeding it here would flip it into allow-list mode and
        // revoke all its OTHER sub-views.
        const target = subViews[newParent];
        if (!target?.length) return;
        if (!target.includes(newSub)) target.push(newSub);
      });
    });
  });

  return { views, subViews };
}

/**
 * Split a stored `val` string into current top-level AclViewKeys only
 * (sub-view keys are dropped). Legacy keys normalized.
 */
export function parseAclViewsVal(val: string): AclViewKey[] {
  return parseAclRights(val).views;
}

/**
 * Whether a level's parsed rights grant access to a specific sub-view.
 *
 * Legacy fallback: if the parent is granted but the level stores NO sub-view
 * keys for it, every sub-view is allowed. Once any sub-view key is stored the
 * check switches to allow-list mode.
 */
export function hasSubViewAccess(parsed: ParsedAclViews, parent: AclViewKey, sub: string): boolean {
  if (!parsed.views.includes(parent)) return false;
  const subs = parsed.subViews[parent];
  if (!subs || subs.length === 0) return true; // legacy / unrestricted
  return subs.includes(sub);
}

/**
 * Effective sub-view keys a level may access for a parent (legacy fallback =
 * all defined sub-views). Returns [] if the parent itself is not granted.
 */
export function effectiveSubViews(parsed: ParsedAclViews, parent: AclViewKey): string[] {
  if (!parsed.views.includes(parent)) return [];
  const defined = subViewKeys(parent);
  if (defined.length === 0) return [];
  const subs = parsed.subViews[parent];
  if (!subs || subs.length === 0) return defined; // legacy / unrestricted
  return defined.filter(k => subs.includes(k));
}

/** Ordered list of entries for rendering in the admin UI */
export const ACL_VIEW_ENTRIES = Object.entries(ACL_VIEWS) as [AclViewKey, (typeof ACL_VIEWS)[AclViewKey]][];
